import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { Row } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DisplayField from 'components/forms/displayField/DisplayField';
import RichTextEditorField from 'components/forms/richTextEditor/RichTextEditorField';
import TextField from 'components/forms/textField/TextField';
import { getLanguageName } from 'i18n/i18n.utils';
import { LanguageCode } from 'i18n/internationalization.types';
import { i18n } from 'i18n/setupInternationalization';
import { ScheduledMessage } from 'models/automation/ScheduledMessages';
import {
  MessagingType,
  SystemTemplate,
} from 'models/automation/SystemTemplates';
import TriggerEventType from 'pages/automations/common/TriggerEventType';
import TriggerName from 'pages/automations/common/TriggerName';
import TriggerStateIcon from 'pages/automations/common/TriggerStateIcon';
import { ScheduledMessagesEditModalFormValues } from './ScheduledMessagesEditModal.schema';
import { StateStyled } from './ScheduledMessagesEditModal.styles';
import useScheduledMessagesToolbarSettings from './useScheduledMessagesToolbarSettings';

interface ScheduledMessagesEditModalFormElementsProps {
  isOnPreviewMode: boolean;
  scheduledMessage: ScheduledMessage;
  setIsOnPreviewMode: Dispatch<SetStateAction<boolean>>;
  template: SystemTemplate | null;
}

const ScheduledMessagesEditModalFormElements = ({
  isOnPreviewMode,
  scheduledMessage,
  setIsOnPreviewMode,
  template,
}: ScheduledMessagesEditModalFormElementsProps) => {
  const { t } = useTranslation();
  const hasLoaded = useRef(false);
  const { reset, getValues } =
    useFormContext<ScheduledMessagesEditModalFormValues>();

  const messagingType = template?.messagingType;
  const isEmail = messagingType === MessagingType.EMAIL;

  const { toolbarSettings, isLoadingVariables } =
    useScheduledMessagesToolbarSettings({
      messagingType,
      onEnterPreviewMode: () => setIsOnPreviewMode(true),
      onExitPreviewMode: () => setIsOnPreviewMode(false),
      scheduledMessage,
    });

  // This is a hack for making the rich text field value not start as dirty in the form, so the user can still click on
  // the "Send now" button when first opening the edit modal.
  useEffect(() => {
    if (!isLoadingVariables && !hasLoaded.current) {
      reset(getValues());
      hasLoaded.current = true;
    }
  }, [isLoadingVariables]);

  return (
    <Row className="d-flex flex-column">
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="state"
        label={t('pageAutomations.scheduledMessages.modal.messageStatus')}
        value={
          <StateStyled>
            <TriggerStateIcon state={scheduledMessage.state} />
            {t('pageAutomations.triggers.triggerState', {
              context: scheduledMessage.state,
            })}
          </StateStyled>
        }
      />
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="expectedFireTime"
        label={t('pageAutomations.scheduledMessages.modal.scheduledTime')}
        value={t('common.dateTimeLong', {
          date: scheduledMessage.expectedFireTime,
        })}
      />
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="trigger"
        label={t('pageAutomations.scheduledMessages.modal.source')}
        value={
          <TriggerName
            formatName={(name) =>
              t('pageAutomations.scheduledMessages.modal.sourceName', { name })
            }
            triggerUid={scheduledMessage.triggerUid}
          />
        }
      />
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="eventType"
        label={t('pageAutomations.scheduledMessages.modal.event')}
        value={<TriggerEventType triggerUid={scheduledMessage.triggerUid} />}
      />
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="templateType"
        label={t('pageAutomations.scheduledMessages.modal.templateType')}
        value={t('pageAutomations.systemTemplates.messagingType', {
          context: messagingType,
        })}
      />
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="templateName"
        label={t('pageAutomations.scheduledMessages.modal.templateName')}
        value={template?.name ?? '-'}
      />
      <DisplayField
        colSmInput={8}
        colSmLabel={4}
        colXsInput={8}
        colXsLabel={4}
        id="language"
        label={t('pageAutomations.scheduledMessages.modal.language')}
        value={getLanguageName(
          i18n.language,
          scheduledMessage.template?.templateLanguage ?? LanguageCode.EN,
        )}
      />
      <TextField
        colSmInput={8}
        colSmLabel={4}
        disabled
        label={t('pageAutomations.scheduledMessages.modal.recipient')}
        name="recipient"
        value={scheduledMessage.triggerRecipients?.join(', ') ?? '-'}
      />
      {isEmail && (
        <TextField
          colSmInput={8}
          colSmLabel={4}
          disabled={isOnPreviewMode}
          label={t('pageAutomations.scheduledMessages.modal.subject')}
          name="templateSubject"
        />
      )}
      <RichTextEditorField
        colSmInput={12}
        colSmLabel={0}
        isLoading={isLoadingVariables}
        linkMode="edit"
        name="templateContent"
        toolbarSettings={toolbarSettings}
      />
    </Row>
  );
};

export default ScheduledMessagesEditModalFormElements;
